import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Upload, Save, ImageIcon, Trash2 } from "lucide-react";

const FIELDS = [
  { key: "company_name", label: "Company / Firm Name", full: true },
  { key: "reg_address", label: "Registered Address", area: true, full: true },
  { key: "mfg_unit", label: "Manufacturing Unit Address", area: true, full: true },
  { key: "llpin", label: "LLPIN / CIN" },
  { key: "gstin", label: "GSTIN" },
  { key: "udyam", label: "Udyam (MSME) Registration No." },
  { key: "dpiit", label: "DPIIT Recognition No." },
  { key: "signatory_name", label: "Authorised Signatory Name" },
  { key: "signatory_designation", label: "Signatory Designation" },
  { key: "email", label: "Contact Email" },
  { key: "phone", label: "Contact Phone" },
];

export default function CompanyProfile() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stamps, setStamps] = useState([]);
  const fileRef = useRef();
  const stampRef = useRef();

  const loadStamps = () => api.get("/stamps").then((r) => setStamps(r.data));
  useEffect(() => { loadStamps(); }, []);

  useEffect(() => {
    api.get("/company").then((r) => setForm(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company", form);
      toast.success("Company letterhead saved");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/company/logo", fd);
      set("logo_path", r.data.logo_path);
      toast.success("Logo uploaded");
    } catch {
      toast.error("Logo upload failed");
    }
  };

  const uploadStamp = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/company/stamp", fd);
      toast.success("Stamp added — documents rotate through all your stamps");
      loadStamps();
    } catch {
      toast.error("Stamp upload failed");
    }
    e.target.value = "";
  };

  const deleteStamp = async (id) => {
    await api.delete(`/stamps/${id}`);
    toast.success("Stamp removed");
    loadStamps();
  };

  if (!form) return <div className="p-12 text-slate-500">Loading…</div>;

  const idline = form.gstin ? `GSTIN: ${form.gstin}` : "";
  const logoUrl = form.logo_path ? `${process.env.REACT_APP_BACKEND_URL}/api/company/logo?t=${form.logo_path.slice(-8)}` : null;

  return (
    <div className="px-6 lg:px-12 py-8 lg:py-12 max-w-7xl">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
          <Building2 className="h-5 w-5 text-slate-900" />
        </div>
        <div>
          <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">Company Letterhead</h1>
          <p className="text-sm text-slate-500">Saved once and printed on top of every document you generate.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mt-8">
        <Card className="p-6 rounded-xl border-border">
          <div className="grid sm:grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <Label className="text-xs font-semibold text-slate-600">{f.label}</Label>
                {f.area ? (
                  <Textarea
                    data-testid={`company-${f.key}`}
                    value={form[f.key] || ""}
                    onChange={(e) => set(f.key, e.target.value)}
                    className="mt-1.5 resize-none"
                    rows={2}
                  />
                ) : (
                  <Input
                    data-testid={`company-${f.key}`}
                    value={form[f.key] || ""}
                    onChange={(e) => set(f.key, e.target.value)}
                    className="mt-1.5"
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 mt-6 flex-wrap">
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadLogo} data-testid="company-logo-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-lg" data-testid="company-logo-btn">
              <Upload className="h-4 w-4 mr-2" /> {form.logo_path ? "Replace Logo" : "Upload Logo"}
            </Button>
            <input ref={stampRef} type="file" accept="image/*" hidden onChange={uploadStamp} data-testid="company-stamp-input" />
            <Button variant="outline" onClick={() => stampRef.current?.click()} className="rounded-lg" data-testid="company-stamp-btn">
              <Upload className="h-4 w-4 mr-2" /> Add Stamp
            </Button>
            <Button onClick={save} disabled={saving} className="rounded-lg bg-slate-900 hover:bg-slate-800 ml-auto" data-testid="company-save-btn">
              <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save Letterhead"}
            </Button>
          </div>
        </Card>

        {/* Live letterhead preview */}
        <Card className="p-8 rounded-xl border-border bg-white h-fit">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-700 mb-4">Live Preview</div>
          <div className="flex items-start gap-4">
            {logoUrl ? (
              <img src={logoUrl} alt="logo" className="h-16 w-auto object-contain" />
            ) : (
              <div className="h-16 w-16 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                <ImageIcon className="h-6 w-6 text-slate-400" />
              </div>
            )}
            <div className="min-w-0">
              <div className="font-head text-xl font-extrabold tracking-tight">{form.company_name || "Your Company Name"}</div>
              {form.mfg_unit && <div className="text-[11px] text-slate-600 mt-1">Mfg. Unit: {form.mfg_unit}</div>}
              {idline && <div className="text-[11px] text-slate-600">{idline}</div>}
            </div>
          </div>
          <div className="border-t-2 border-emerald-600 mt-3" />
          <div className="mt-6 space-y-1 text-[11px] text-slate-500">
            <div><span className="font-semibold text-slate-700">Signatory:</span> {form.signatory_name} — {form.signatory_designation}</div>
            <div><span className="font-semibold text-slate-700">Contact:</span> {form.email} {form.phone}</div>
          </div>
          {stamps.length > 0 && (
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-700 mb-2">
                Stamps ({stamps.length}) — rotated across documents
              </div>
              <div className="flex flex-wrap gap-3" data-testid="company-stamps-gallery">
                {stamps.map((s) => (
                  <div key={s.id} className="relative group" data-testid={`company-stamp-${s.id}`}>
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/stamps/${s.id}/file`}
                      alt="stamp"
                      className="h-20 w-20 object-contain border border-border rounded-lg p-1 bg-white"
                    />
                    <button
                      onClick={() => deleteStamp(s.id)}
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white border border-border shadow flex items-center justify-center text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-stamp-${s.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
